// apps/radar/src/main.tsx
// Version: 2026.4.24
// ForgeRadar Console entry point. Mirrors apps/web/src/main.tsx — same MSAL
// + locale wrapping, plus the Living Engine global stylesheet.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { ForgeTrackAuthProvider, type ForgeTrackAuthConfig } from '@forgetrack/auth';
import { requiredEnv } from '@forgetrack/api-client';
import { LocaleProvider } from '@forgetrack/i18n';
import { ErrorBoundary } from '@forgetrack/engine';
import { routeTree } from './routeTree.gen';

// ── Global styles ─────────────────────────────────────────────────────────
const globalStyles = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; text-rendering: optimizeLegibility; scrollbar-gutter: stable; }
body {
  margin: 0; padding: 0; min-height: 100vh; overflow-x: hidden;
  background: #050910; color: #e8ecf2;
  font-family: 'Outfit', system-ui, -apple-system, sans-serif;
}
::-webkit-scrollbar { width: 6px; height: 6px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.22); }
::selection { background: rgba(34,211,238,0.3); color: #e8ecf2; }
:focus-visible { outline: 2px solid rgba(34,211,238,0.5); outline-offset: 2px; }
button { font-family: inherit; }
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
}
`;
const styleEl = document.createElement('style');
styleEl.textContent = globalStyles;
document.head.appendChild(styleEl);

// ── Router ────────────────────────────────────────────────────────────────
const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router; }
}

// ── MSAL config (ForgeRadar Tenant B Console) ─────────────────────────────
//
// CIAM is unused in Phase 1 — we authenticate against workforce Entra in
// Tenant B (loanforgeoutlook). GitHub Actions injects empty strings for
// unset secrets, so `??` won't fall back; coerce empty → undefined here so
// buildMsalConfig builds the standard https://login.microsoftonline.com/
// authority from the tenantId.
const authConfig: ForgeTrackAuthConfig = {
  clientId: requiredEnv('VITE_AZURE_CLIENT_ID', import.meta.env.VITE_AZURE_CLIENT_ID),
  tenantId: requiredEnv('VITE_AZURE_TENANT_ID', import.meta.env.VITE_AZURE_TENANT_ID),
  redirectUri: window.location.origin,
  scopes: [`${requiredEnv('VITE_API_AUDIENCE', import.meta.env.VITE_API_AUDIENCE)}/.default`],
  appType: 'web',
  authority: import.meta.env.VITE_CIAM_AUTHORITY || undefined,
  knownAuthorities: import.meta.env.VITE_CIAM_KNOWN_AUTHORITY
    ? [import.meta.env.VITE_CIAM_KNOWN_AUTHORITY]
    : [],
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary label="ForgeRadar Console">
      <ForgeTrackAuthProvider config={authConfig}>
        <LocaleProvider>
          <RouterProvider router={router} />
        </LocaleProvider>
      </ForgeTrackAuthProvider>
    </ErrorBoundary>
  </StrictMode>,
);
